import React from 'react';
import PropTypes from 'prop-types';
import { Popper, Paper, Box, Typography, ClickAwayListener, MenuList, Divider } from '@mui/material';
import { Icon } from '@iconify/react';
import { COLORS } from './styles';

// Tier 2 / shared infra — generic Popper-anchored autocomplete list
// reused by the slash, colon, and at-sign composer triggers.
//
// Behaviour parity:
//   - Up / Down arrows are handled by the parent's keydown handler;
//     this component only renders. Highlight is driven by the
//     `selectedIndex` prop.
//   - Click-to-select fires onSelect(item, index).
//   - Click outside or Escape (handled in the parent) closes.
//
// Accessibility:
//   - role="listbox" on the wrapper, aria-selected on each row.
//   - Each row exposes its label so screen readers can speak it.

const ComposerAutocompletePopover = ({
    open,
    anchorEl,
    items,
    selectedIndex,
    onIndexChange,
    onSelect,
    onClose,
    getKey,
    getLabel,
    getDescription,
    placement,
    emptyLabel,
    footerAction,
}) => {
    const listRef = React.useRef(null);

    // Scroll the highlighted item into view as the user navigates
    // with the arrow keys, so long lists feel responsive.
    React.useEffect(() => {
        if (!open) return;
        const list = listRef.current;
        if (!list) return;
        const el = list.querySelector(`[data-idx="${selectedIndex}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
        }
    }, [selectedIndex, open]);

    if (!open || !anchorEl) return null;

    const handleClickAway = () => {
        if (typeof onClose === 'function') onClose();
    };

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement={placement}
            modifiers={[
                { name: 'offset', options: { offset: [0, 6] } },
                { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
            ]}
            style={{ zIndex: 1300 }}
        >
            <ClickAwayListener onClickAway={handleClickAway}>
                <Paper
                    elevation={6}
                    sx={{
                        minWidth: 240,
                        maxWidth: 360,
                        maxHeight: 280,
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: `1px solid ${COLORS.divider}`,
                    }}
                >
                    {items.length === 0 ? (
                        <Box sx={{ padding: '12px 14px', fontSize: '13px', color: COLORS.textSecondary }}>
                            {emptyLabel}
                        </Box>
                    ) : (
                        <MenuList
                            ref={listRef}
                            role="listbox"
                            aria-label="Autocomplete suggestions"
                            sx={{
                                py: 0.5,
                                maxHeight: 280,
                                overflowY: 'auto',
                            }}
                        >
                            {items.map((item, idx) => {
                                const key = getKey ? getKey(item, idx) : idx;
                                const label = getLabel ? getLabel(item) : String(item);
                                const description = getDescription ? getDescription(item) : null;
                                const isSelected = idx === selectedIndex;
                                return (
                                    <Box
                                        key={key}
                                        data-idx={idx}
                                        role="option"
                                        aria-selected={isSelected}
                                        onMouseDown={(e) => {
                                            // Use mousedown (not click) so we beat the
                                            // textarea blur — keeps focus where it is.
                                            e.preventDefault();
                                            onSelect(item, idx);
                                        }}
                                        onMouseEnter={() => {
                                            if (typeof onIndexChange === 'function') onIndexChange(idx);
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 1.5,
                                            py: 0.85,
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? COLORS.primaryLighter : 'transparent',
                                            '&:hover': {
                                                backgroundColor: COLORS.primaryLighter,
                                            },
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: COLORS.textPrimary,
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {label}
                                            </Typography>
                                            {description ? (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: COLORS.textSecondary,
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {description}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </MenuList>
                    )}
                    {footerAction ? (
                        <>
                            <Divider sx={{ my: 0 }} />
                            <Box
                                role="button"
                                tabIndex={0}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    footerAction.onClick?.();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        footerAction.onClick?.();
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 1,
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    color: COLORS.textSecondary,
                                    '&:hover': { backgroundColor: COLORS.primaryLighter, color: COLORS.textPrimary },
                                }}
                            >
                                <Icon icon={footerAction.icon || 'mdi:cog-outline'} width={14} height={14} />
                                <Typography component="span" sx={{ fontSize: '12px', fontWeight: 500 }}>
                                    {footerAction.label}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Icon icon="mdi:arrow-top-right" width={12} height={12} />
                            </Box>
                        </>
                    ) : null}
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
};

ComposerAutocompletePopover.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.any,
    items: PropTypes.array,
    selectedIndex: PropTypes.number,
    onIndexChange: PropTypes.func,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    getKey: PropTypes.func,
    getLabel: PropTypes.func,
    getDescription: PropTypes.func,
    placement: PropTypes.string,
    emptyLabel: PropTypes.string,
    footerAction: PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.string,
        onClick: PropTypes.func.isRequired,
    }),
};

ComposerAutocompletePopover.defaultProps = {
    anchorEl: null,
    items: [],
    selectedIndex: 0,
    onIndexChange: undefined,
    onClose: undefined,
    getKey: undefined,
    getLabel: undefined,
    getDescription: undefined,
    placement: 'top-start',
    emptyLabel: 'No matches',
    footerAction: null,
};

export default ComposerAutocompletePopover;
